import * as THREE from "three";

import {
  batchStaticMeshes,
  collectAndDisposeOwnedResources,
  createGroundPatchGeometry,
  createLeafGeometry,
  createMesh,
  createPearGeometry,
  createRibbonGeometry,
  createRoundedBlockGeometry,
  createSunpatchMaterials,
  createWedgeGeometry,
  createWingGeometry,
  nameNode,
  setAuthoredMetadata,
  type SunpatchMaterials,
} from "./geometry.ts";

export interface FirstYardPlayerRig {
  readonly root: THREE.Group;
  readonly body: THREE.Group;
  readonly leftArm: THREE.Group;
  readonly rightArm: THREE.Group;
  readonly leftLeg: THREE.Group;
  readonly rightLeg: THREE.Group;
  readonly dustAnchor: THREE.Object3D;
}

export interface FirstYardChickenRig {
  readonly root: THREE.Group;
  readonly head: THREE.Group;
  readonly leftWing: THREE.Group;
  readonly rightWing: THREE.Group;
}

export interface FirstYardCornRig {
  readonly root: THREE.Group;
  readonly leaves: THREE.Group;
}

export interface FirstYardPlotRig extends FirstYardCornRig {
  readonly cooldownCue: THREE.Group;
  readonly clearedCue: THREE.Group;
}

export interface FirstYardCargoUnitRig {
  readonly root: THREE.Group;
  readonly corn: THREE.Group;
  readonly egg: THREE.Group;
}

export interface FirstYardManualChainRig {
  readonly plots: readonly FirstYardPlotRig[];
  readonly cargo: readonly FirstYardCargoUnitRig[];
  readonly stagedCorn: readonly THREE.Group[];
  readonly nestEggs: readonly THREE.Group[];
  readonly marketReceiver: THREE.Group;
  readonly marketCash: THREE.Group;
  readonly penExtensionPad: Readonly<{
    root: THREE.Group;
    locked: THREE.Group;
    active: THREE.Group;
    ring: THREE.Mesh;
    chevrons: THREE.Group;
  }>;
}

export interface FirstYardGuidedPad {
  readonly root: THREE.Group;
  readonly ring: THREE.Mesh;
  readonly chevrons: THREE.Group;
}

interface FirstYardMarketRig {
  readonly receiver: THREE.Group;
  readonly cash: THREE.Group;
}

export interface FirstYardVisualKit {
  readonly root: THREE.Group;
  readonly player: FirstYardPlayerRig;
  readonly chickens: readonly FirstYardChickenRig[];
  readonly corn: readonly FirstYardPlotRig[];
  readonly manualChain: FirstYardManualChainRig;
  readonly guidedPad: FirstYardGuidedPad;
  readonly occluders: readonly THREE.Object3D[];
  dispose(): void;
}

type SharedGeometry = ReturnType<typeof createSharedGeometry>;

function createSharedGeometry() {
  const fencePost = createRoundedBlockGeometry(0.17, 0.72, 0.17, 0.045);
  const fenceRail = createRoundedBlockGeometry(0.98, 0.13, 0.1, 0.035);
  const plankWide = createRoundedBlockGeometry(0.92, 0.17, 0.12, 0.035);
  const plankMedium = createRoundedBlockGeometry(0.68, 0.15, 0.1, 0.03);
  const plankNarrow = createRoundedBlockGeometry(0.42, 0.12, 0.08, 0.025);
  const sign = createRoundedBlockGeometry(0.72, 0.54, 0.09, 0.1);
  const leaf = createLeafGeometry(0.22, 0.66, 0.028);
  const cornLeaf = createLeafGeometry(0.2, 0.72, 0.025);
  const cornStalk = new THREE.CylinderGeometry(0.035, 0.055, 0.82, 7);
  const cornCob = createPearGeometry(0.25, 0.52, 12);
  const chickenBody = createPearGeometry(0.48, 0.58, 14);
  const chickenHead = new THREE.SphereGeometry(0.25, 14, 10);
  const chickenWing = createWingGeometry(0.43, 0.44, 0.045);
  const chickenFoot = createWedgeGeometry(0.2, 0.08, 0.28);
  const chickenBeak = createWedgeGeometry(0.16, 0.12, 0.24);
  const egg = createPearGeometry(0.18, 0.28, 12);
  const smallRock = new THREE.DodecahedronGeometry(0.16, 0);
  const tuftLeaf = createLeafGeometry(0.14, 0.42, 0.018);
  const boot = createRoundedBlockGeometry(0.29, 0.18, 0.42, 0.075);
  const mitten = createPearGeometry(0.22, 0.31, 12);
  return {
    boot,
    chickenBeak,
    chickenBody,
    chickenFoot,
    chickenHead,
    chickenWing,
    cornCob,
    cornLeaf,
    cornStalk,
    egg,
    fencePost,
    fenceRail,
    leaf,
    mitten,
    plankMedium,
    plankNarrow,
    plankWide,
    sign,
    smallRock,
    tuftLeaf,
  };
}

function addMesh(
  parent: THREE.Object3D,
  name: string,
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  position: readonly [number, number, number],
  rotation: readonly [number, number, number] = [0, 0, 0],
  scale: readonly [number, number, number] = [1, 1, 1],
): THREE.Mesh {
  const mesh = createMesh(name, geometry, material);
  mesh.position.set(...position);
  mesh.rotation.set(...rotation);
  mesh.scale.set(...scale);
  parent.add(mesh);
  return mesh;
}

function createPathNetwork(root: THREE.Group, materials: SunpatchMaterials): void {
  const paths = nameNode(new THREE.Group(), "landmark-authored-path-ribbons");
  const pathSpecs: readonly (readonly [string, readonly THREE.Vector3[], number])[] = [
    ["path-field-to-pen", [new THREE.Vector3(-4.15, 0.015, -1.48), new THREE.Vector3(-2.2, 0.015, -1.57), new THREE.Vector3(-0.45, 0.015, -1.38), new THREE.Vector3(1, 0.015, -1.47)], 0.47],
    ["path-pen-to-market", [new THREE.Vector3(1, 0.012, -1.47), new THREE.Vector3(0.25, 0.012, -0.12), new THREE.Vector3(-0.4, 0.012, 1.55), new THREE.Vector3(-1.551, 0.012, 2.945)], 0.48],
    ["path-market-to-field", [new THREE.Vector3(-1.551, 0.01, 2.945), new THREE.Vector3(-2.65, 0.01, 1.88), new THREE.Vector3(-3.25, 0.01, 0.42), new THREE.Vector3(-4.102, 0.01, -1.473)], 0.44],
    ["path-market-to-locked-gate", [new THREE.Vector3(-1.551, 0.008, 2.945), new THREE.Vector3(-0.9, 0.008, 3.72), new THREE.Vector3(-0.15, 0.008, 4.55), new THREE.Vector3(0, 0.008, 5.34)], 0.5],
  ];
  for (const [name, points, width] of pathSpecs) {
    const shadow = createRibbonGeometry(points, width + 0.075, 0.065);
    addMesh(paths, `${name}-modeled-edge`, shadow, materials.pathEdge, [0, -0.018, 0], [0, 0, 0], [1, 1, 1]).receiveShadow = true;
    const face = createRibbonGeometry(points, width, 0.048);
    addMesh(paths, `${name}-warm-worn-face`, face, materials.path, [0, 0.014, 0], [0, 0, 0], [1, 1, 1]).receiveShadow = true;
  }
  setAuthoredMetadata(paths, "first-yard-path-network");
  root.add(paths);
}

function createGround(root: THREE.Group, materials: SunpatchMaterials, shared: SharedGeometry): void {
  const ground = nameNode(new THREE.Group(), "landmark-first-yard-ground");
  addMesh(ground, "first-yard-rounded-clipped-earth-base", createGroundPatchGeometry(10.75, 9.35, 0.34, 0.56, 0.78), materials.soilDark, [0, -0.12, 0.92]);
  addMesh(ground, "first-yard-cultivated-grass-face", createGroundPatchGeometry(10.45, 9.05, 0.2, 0.52, 0.72), materials.grass, [0, 0.02, 0.88]);

  const dryPatches = [
    [-4.55, -2.92, 1.05, 0.42, -0.08],
    [4.25, 0.9, 0.92, 0.34, 0.22],
    [-4.6, 3.86, 0.78, 0.3, -0.3],
    [3.72, 4.53, 0.86, 0.32, 0.16],
  ] as const;
  for (let index = 0; index < dryPatches.length; index += 1) {
    const [x, z, sx, sz, rotation] = dryPatches[index]!;
    addMesh(ground, `desperate-dry-patch-${index + 1}`, createGroundPatchGeometry(1, 1, 0.025, 0.22, 0.18), materials.grassDry, [x, 0.125, z], [0, rotation, 0], [sx, 1, sz]);
  }

  const rocks = new THREE.InstancedMesh(shared.smallRock, materials.stone, 18);
  rocks.name = "perimeter-authored-rock-cluster";
  rocks.castShadow = true;
  rocks.receiveShadow = true;
  const rockTransforms = [
    [-4.9, 0.22, -3.1, 1.3, 0.4], [-3.9, 0.16, -3.35, 0.8, -0.2], [-2.2, 0.14, -3.38, 0.72, 0.7],
    [2.2, 0.15, -3.36, 0.76, 0.2], [4.7, 0.24, -2.9, 1.25, -0.4], [5.08, 0.17, -1.1, 0.82, 0.2],
    [5.12, 0.18, 1.8, 0.95, 0.6], [4.8, 0.22, 4.82, 1.2, 0.3], [3.25, 0.16, 5.25, 0.82, 0.8],
    [-2.3, 0.16, 5.24, 0.82, -0.4], [-4.6, 0.23, 4.94, 1.15, 0.1], [-5.12, 0.17, 3.2, 0.9, 0.5],
    [-5.14, 0.14, 0.8, 0.7, -0.3], [-5.1, 0.18, -1.4, 0.9, 0.7], [-3.2, 0.13, 4.72, 0.68, 0],
    [4.6, 0.12, 3.62, 0.62, 0.4], [3.8, 0.12, -2.7, 0.66, 0.1], [-4.5, 0.12, 2.15, 0.62, -0.5],
  ] as const;
  const matrix = new THREE.Matrix4();
  for (let index = 0; index < rockTransforms.length; index += 1) {
    const [x, y, z, scale, yaw] = rockTransforms[index]!;
    matrix.compose(new THREE.Vector3(x, y, z), new THREE.Quaternion().setFromEuler(new THREE.Euler(0, yaw, 0)), new THREE.Vector3(scale, scale * 0.72, scale));
    rocks.setMatrixAt(index, matrix);
  }
  rocks.instanceMatrix.needsUpdate = true;
  ground.add(rocks);
  setAuthoredMetadata(ground, "first-yard-ground");
  root.add(ground);
}

function createFenceSegment(
  parent: THREE.Object3D,
  name: string,
  shared: SharedGeometry,
  materials: SunpatchMaterials,
  x: number,
  z: number,
  yaw: number,
  repaired = false,
): void {
  const segment = nameNode(new THREE.Group(), name);
  segment.position.set(x, 0.09, z);
  segment.rotation.y = yaw;
  addMesh(segment, `${name}-post-a`, shared.fencePost, repaired ? materials.woodLight : materials.wood, [-0.49, 0.36, 0]);
  addMesh(segment, `${name}-post-b`, shared.fencePost, repaired ? materials.woodLight : materials.wood, [0.49, 0.36, 0]);
  addMesh(segment, `${name}-rail-upper`, shared.fenceRail, materials.wood, [0, 0.52, 0], [0, 0, repaired ? 0.015 : -0.035]);
  addMesh(segment, `${name}-rail-lower`, shared.fenceRail, repaired ? materials.barn : materials.woodDark, [0, 0.25, 0], [0, 0, repaired ? -0.012 : 0.03]);
  parent.add(segment);
}

function createPerimeter(root: THREE.Group, materials: SunpatchMaterials, shared: SharedGeometry): void {
  const perimeter = nameNode(new THREE.Group(), "landmark-detailed-yard-perimeter");
  for (let index = 0; index < 8; index += 1) {
    createFenceSegment(perimeter, `south-fence-${index + 1}`, shared, materials, -4.35 + index * 1.24, -3.31, 0, index === 3);
  }
  for (let index = 0; index < 7; index += 1) {
    createFenceSegment(perimeter, `west-fence-${index + 1}`, shared, materials, -5.05, -2.55 + index * 1.18, Math.PI / 2, index === 4);
    createFenceSegment(perimeter, `east-fence-${index + 1}`, shared, materials, 5.05, -2.55 + index * 1.18, Math.PI / 2, index === 1);
  }
  for (let index = 0; index < 3; index += 1) {
    createFenceSegment(perimeter, `northwest-fence-${index + 1}`, shared, materials, -4.35 + index * 1.18, 5.13, 0, false);
    createFenceSegment(perimeter, `northeast-fence-${index + 1}`, shared, materials, 2 + index * 1.18, 5.13, 0, index === 2);
  }
  setAuthoredMetadata(perimeter, "first-yard-detailed-perimeter");
  root.add(perimeter);
}

function createFoliage(root: THREE.Group, materials: SunpatchMaterials, shared: SharedGeometry, occluders: THREE.Object3D[]): void {
  const foliage = nameNode(new THREE.Group(), "landmark-perimeter-foliage-and-props");
  const tuftSpecs = [
    [-4.75, -2.72], [-4.34, -2.96], [-3.25, -3.1], [-1.2, -3.17], [1.1, -3.2], [4.35, -2.8], [4.7, -2.2],
    [4.82, 0.2], [4.65, 2.05], [4.38, 4.35], [3.22, 4.93], [2.12, 4.98], [-2.1, 5.02], [-3.35, 4.88],
    [-4.65, 4.4], [-4.77, 3.25], [-4.82, 1.2], [-4.78, -0.72], [-3.9, 3.85], [3.9, 3.85],
    [4.0, -1.0], [-3.7, 0.35], [-2.8, -2.65], [2.8, -2.75], [-4.15, 2.42], [3.65, 2.78],
  ] as const;
  const tufts = new THREE.InstancedMesh(shared.tuftLeaf, materials.grassDark, tuftSpecs.length * 3);
  tufts.name = "dense-nonobstructive-authored-tufts";
  tufts.castShadow = true;
  const matrix = new THREE.Matrix4();
  let instanceIndex = 0;
  for (let index = 0; index < tuftSpecs.length; index += 1) {
    const [x, z] = tuftSpecs[index]!;
    for (let blade = 0; blade < 3; blade += 1) {
      const yaw = ((index * 5 + blade * 4) % 13) * 0.21;
      const scale = 0.78 + ((index + blade * 2) % 5) * 0.055;
      matrix.compose(
        new THREE.Vector3(x + (blade - 1) * 0.085, 0.12, z + (blade % 2) * 0.06),
        new THREE.Quaternion().setFromEuler(new THREE.Euler(-0.08 + blade * 0.08, yaw, 0.16 - blade * 0.15)),
        new THREE.Vector3(scale, scale, scale),
      );
      tufts.setMatrixAt(instanceIndex, matrix);
      instanceIndex += 1;
    }
  }
  tufts.instanceMatrix.needsUpdate = true;
  foliage.add(tufts);

  const treeSpecs = [
    [4.55, 4.48, 1.0], [-4.5, 4.18, 1.08], [4.65, -2.4, 0.82], [-4.72, -2.5, 0.86],
  ] as const;
  const trunkGeometry = new THREE.CylinderGeometry(0.17, 0.25, 1.5, 8);
  const canopyGeometry = new THREE.DodecahedronGeometry(0.72, 1);
  for (let index = 0; index < treeSpecs.length; index += 1) {
    const [x, z, scale] = treeSpecs[index]!;
    const tree = nameNode(new THREE.Group(), `perimeter-tree-${index + 1}`);
    tree.position.set(x, 0.1, z);
    tree.scale.setScalar(scale);
    addMesh(tree, `tree-${index + 1}-tapered-trunk`, trunkGeometry, materials.woodDark, [0, 0.75, 0], [0, 0, index % 2 === 0 ? 0.08 : -0.1]);
    const canopy = nameNode(new THREE.Group(), `tree-${index + 1}-layered-canopy`);
    addMesh(canopy, `tree-${index + 1}-canopy-center`, canopyGeometry, materials.grassDark, [0, 1.64, 0], [0.1, index * 0.4, 0.1], [1.08, 0.82, 1.0]);
    addMesh(canopy, `tree-${index + 1}-canopy-left`, canopyGeometry, materials.leafLight, [-0.42, 1.55, 0.04], [0.2, index * 0.2, 0], [0.68, 0.64, 0.68]);
    addMesh(canopy, `tree-${index + 1}-canopy-right`, canopyGeometry, materials.grass, [0.4, 1.62, -0.08], [0, -index * 0.23, 0.15], [0.7, 0.66, 0.68]);
    tree.add(canopy);
    foliage.add(tree);
    occluders.push(canopy);
  }

  const trough = nameNode(new THREE.Group(), "decorative-empty-water-trough");
  trough.position.set(4.1, 0.15, 1.45);
  addMesh(trough, "trough-body", createRoundedBlockGeometry(1.2, 0.4, 0.48, 0.12), materials.wood, [0, 0.28, 0]);
  addMesh(trough, "trough-empty-recess", createRoundedBlockGeometry(0.9, 0.18, 0.32, 0.08), materials.ink, [0, 0.38, 0.1]);
  foliage.add(trough);
  setAuthoredMetadata(foliage, "first-yard-foliage-props");
  root.add(foliage);
}

function createField(root: THREE.Group, materials: SunpatchMaterials, shared: SharedGeometry): readonly FirstYardPlotRig[] {
  const field = nameNode(new THREE.Group(), "landmark-field-row-01");
  field.position.set(-4.102, 0, -1.473);
  const bed = addMesh(field, "field-rounded-clipped-soil-bed", createGroundPatchGeometry(3.05, 3.0, 0.22, 0.34, 0.46), materials.soil, [0, 0.04, 0], [0, -0.06, 0]);
  bed.receiveShadow = true;
  const stoneEdgingGeometry = createRoundedBlockGeometry(0.42, 0.18, 0.2, 0.075);
  const edging = [
    [-1.27, -1.3, 0.05], [-0.82, -1.43, -0.02], [-0.36, -1.48, 0.02], [0.12, -1.46, -0.03], [0.58, -1.4, 0.04], [1.04, -1.26, -0.05],
    [-1.42, -0.78, 1.55], [-1.45, -0.25, 1.57], [-1.45, 0.3, 1.58], [-1.38, 0.85, 1.6],
    [-1.15, 1.27, 0.08], [-0.68, 1.43, -0.03], [-0.2, 1.48, 0.04], [0.28, 1.46, -0.02], [0.77, 1.4, 0.04], [1.15, 1.25, -0.05],
  ] as const;
  for (let index = 0; index < edging.length; index += 1) {
    const [x, z, rotation] = edging[index]!;
    addMesh(field, `field-modeled-edge-stone-${index + 1}`, stoneEdgingGeometry, index % 3 === 0 ? materials.stoneLight : materials.stone, [x, 0.16, z], [Math.PI / 2, rotation, 0], [1, 1, 1]);
  }

  const corn: FirstYardPlotRig[] = [];
  const cornSpecs = [
    [-0.95, -0.88, -0.1, 1], [-0.28, -0.98, 0.08, 0.96], [0.42, -0.86, -0.04, 1.04], [0.94, -0.62, 0.12, 0.98],
    [-1.02, -0.08, 0.05, 1.03], [-0.34, -0.2, -0.1, 0.97], [0.37, -0.12, 0.12, 1.01], [0.91, 0.07, -0.08, 0.96],
  ] as const;
  for (let index = 0; index < cornSpecs.length; index += 1) {
    const [x, z, lean, scale] = cornSpecs[index]!;
    const rootNode = nameNode(new THREE.Group(), `ripe-corn-${String(index + 1).padStart(2, "0")}`);
    rootNode.position.set(x, 0.14, z);
    rootNode.rotation.z = lean;
    rootNode.scale.setScalar(scale);
    addMesh(rootNode, `${rootNode.name}-stalk`, shared.cornStalk, materials.leaf, [0, 0.45, 0]);
    const leaves = nameNode(new THREE.Group(), `${rootNode.name}-sway-leaves`);
    addMesh(leaves, `${rootNode.name}-leaf-left`, shared.cornLeaf, materials.leaf, [-0.02, 0.18, 0], [0.03, -0.72, 0.52], [1, 1, 1]);
    addMesh(leaves, `${rootNode.name}-leaf-right`, shared.cornLeaf, materials.leafLight, [0.02, 0.2, 0], [-0.02, 0.72, -0.49], [0.92, 0.94, 0.92]);
    rootNode.add(leaves);
    addMesh(rootNode, `${rootNode.name}-gold-crown`, shared.cornCob, index % 2 === 0 ? materials.corn : materials.cornShadow, [0, 0.55, 0.01], [0.07, index * 0.63, 0.02]);
    setAuthoredMetadata(rootNode, `first-yard-ripe-corn-${index + 1}`);
    field.add(rootNode);
    const clearedCue = nameNode(new THREE.Group(), `plot-${String(index + 1).padStart(2, "0")}-cleared-cue`);
    clearedCue.position.set(x, 0.17, z);
    clearedCue.visible = false;
    addMesh(clearedCue, `${clearedCue.name}-cut-stalk-a`, new THREE.CylinderGeometry(0.038, 0.06, 0.19, 6), materials.strawDark, [-0.06, 0.1, 0.02], [0.08, 0, 0.04]);
    addMesh(clearedCue, `${clearedCue.name}-cut-stalk-b`, new THREE.CylinderGeometry(0.032, 0.052, 0.15, 6), materials.straw, [0.07, 0.08, -0.04], [-0.08, 0, -0.05]);
    field.add(clearedCue);

    const cooldownCue = nameNode(new THREE.Group(), `plot-${String(index + 1).padStart(2, "0")}-cooldown-cue`);
    cooldownCue.position.set(x, 0.2, z);
    cooldownCue.visible = false;
    const cooldownRing = addMesh(cooldownCue, `${cooldownCue.name}-growth-ring`, new THREE.TorusGeometry(0.2, 0.032, 6, 18), materials.focus, [0, 0.035, 0], [Math.PI / 2, 0, 0]);
    cooldownRing.userData.presentationRole = "cooldown-progress";
    addMesh(cooldownCue, `${cooldownCue.name}-new-leaf`, shared.cornLeaf, materials.leafLight, [0, 0.13, 0], [0.1, 0.15, 0.7], [0.45, 0.38, 0.45]);
    field.add(cooldownCue);
    corn.push({ clearedCue, cooldownCue, leaves, root: rootNode });
  }

  const sign = createSeedTag("field-seed-tag-sign", shared, materials, "corn");
  sign.position.set(-1.27, 0.2, -1.03);
  sign.rotation.y = 0.25;
  field.add(sign);
  // D-016 owns the landmark transform; the authored bed is intentionally offset around its approach edge.
  for (const child of field.children) {
    child.position.x += 0.452;
    child.position.y += 0.12;
    child.position.z += 0.073;
  }
  setAuthoredMetadata(field, "first-yard-field-row-01");
  root.add(field);
  return corn;
}

function createSeedTag(
  name: string,
  shared: SharedGeometry,
  materials: SunpatchMaterials,
  icon: "corn" | "chicken" | "egg" | "market",
): THREE.Group {
  const sign = nameNode(new THREE.Group(), name);
  addMesh(sign, `${name}-left-post`, shared.plankNarrow, materials.woodDark, [-0.24, 0.35, 0], [0, 0, Math.PI / 2], [0.8, 0.8, 0.8]);
  addMesh(sign, `${name}-right-post`, shared.plankNarrow, materials.woodDark, [0.24, 0.35, 0], [0, 0, Math.PI / 2], [0.8, 0.8, 0.8]);
  addMesh(sign, `${name}-cream-notched-face`, shared.sign, materials.cream, [0, 0.72, 0]);
  const iconRoot = nameNode(new THREE.Group(), `${name}-${icon}-icon`);
  iconRoot.position.set(0, 0.72, 0.065);
  if (icon === "egg") {
    addMesh(iconRoot, `${name}-egg-mark`, shared.egg, materials.ink, [0, -0.14, 0], [Math.PI / 2, 0, 0], [0.65, 0.65, 0.24]);
  } else if (icon === "corn") {
    addMesh(iconRoot, `${name}-corn-mark`, shared.cornCob, materials.ink, [0, -0.15, 0], [Math.PI / 2, 0, 0], [0.48, 0.48, 0.18]);
  } else if (icon === "chicken") {
    addMesh(iconRoot, `${name}-chicken-mark`, shared.chickenBody, materials.ink, [0, -0.16, 0], [Math.PI / 2, 0, 0], [0.45, 0.45, 0.14]);
  } else {
    addMesh(iconRoot, `${name}-market-mark`, shared.plankMedium, materials.ink, [0, 0, 0], [0, 0, 0], [0.45, 0.65, 0.18]);
  }
  sign.add(iconRoot);
  return sign;
}

function createChicken(
  index: number,
  shared: SharedGeometry,
  materials: SunpatchMaterials,
  material: THREE.MeshStandardMaterial,
  x: number,
  z: number,
  yaw: number,
): FirstYardChickenRig {
  const root = nameNode(new THREE.Group(), `chicken-${index}`);
  root.position.set(x, 0.16, z);
  root.rotation.y = yaw;
  addMesh(root, `chicken-${index}-teardrop-body`, shared.chickenBody, material, [0, 0.16, 0], [0, 0, -0.08]);
  const head = nameNode(new THREE.Group(), `chicken-${index}-head-joint`);
  head.position.set(0, 0.63, -0.11);
  addMesh(head, `chicken-${index}-head-form`, shared.chickenHead, material, [0, 0, 0], [0, 0, 0], [0.9, 0.92, 0.86]);
  addMesh(head, `chicken-${index}-beak`, shared.chickenBeak, materials.beak, [0, -0.03, -0.25], [-Math.PI / 2, 0, 0], [0.82, 0.82, 0.82]);
  const combGeometry = createLeafGeometry(0.11, 0.19, 0.035);
  for (let comb = 0; comb < 3; comb += 1) {
    addMesh(head, `chicken-${index}-comb-${comb + 1}`, combGeometry, materials.comb, [(comb - 1) * 0.055, 0.18 + (comb === 1 ? 0.035 : 0), 0], [0, 0, (comb - 1) * -0.2], [1, 1, 1]);
  }
  const eyeGeometry = new THREE.SphereGeometry(0.032, 8, 6);
  addMesh(head, `chicken-${index}-eye-left`, eyeGeometry, materials.ink, [-0.105, 0.045, -0.192]);
  addMesh(head, `chicken-${index}-eye-right`, eyeGeometry, materials.ink, [0.105, 0.045, -0.192]);
  root.add(head);

  const leftWing = nameNode(new THREE.Group(), `chicken-${index}-left-wing-joint`);
  leftWing.position.set(-0.39, 0.38, 0);
  addMesh(leftWing, `chicken-${index}-left-layered-wing`, shared.chickenWing, material, [0, 0, 0], [0, Math.PI / 2, -0.12]);
  root.add(leftWing);
  const rightWing = nameNode(new THREE.Group(), `chicken-${index}-right-wing-joint`);
  rightWing.position.set(0.39, 0.38, 0);
  addMesh(rightWing, `chicken-${index}-right-layered-wing`, shared.chickenWing, material, [0, 0, 0], [0, -Math.PI / 2, 0.12], [-1, 1, 1]);
  root.add(rightWing);

  for (let tail = 0; tail < 3; tail += 1) {
    addMesh(root, `chicken-${index}-fan-tail-${tail + 1}`, shared.leaf, material, [(tail - 1) * 0.13, 0.38 + Math.abs(tail - 1) * -0.06, 0.31], [0.72, (tail - 1) * 0.32, (tail - 1) * 0.3], [0.75, 0.68, 0.75]);
  }
  addMesh(root, `chicken-${index}-left-wedge-foot`, shared.chickenFoot, materials.beak, [-0.17, 0, -0.02], [0, 0, 0]);
  addMesh(root, `chicken-${index}-right-wedge-foot`, shared.chickenFoot, materials.beak, [0.17, 0, -0.02], [0, 0, 0]);
  setAuthoredMetadata(root, `first-yard-chicken-${index}`);
  return { head, leftWing, rightWing, root };
}

function createPen(
  root: THREE.Group,
  materials: SunpatchMaterials,
  shared: SharedGeometry,
  occluders: THREE.Object3D[],
): readonly FirstYardChickenRig[] {
  const pen = nameNode(new THREE.Group(), "landmark-chicken-pen-01");
  addMesh(pen, "pen-straw-scattered-soil", createGroundPatchGeometry(2.62, 2.94, 0.15, 0.26, 0.32), materials.soil, [3, 0.08, -1.37]);
  const fenceSpecs = [
    [2.15, -2.82, 0], [3.15, -2.82, 0], [4.1, -2.82, 0],
    [4.23, -2.32, Math.PI / 2], [4.23, -1.32, Math.PI / 2], [4.23, -0.32, Math.PI / 2],
    [3.72, 0.04, 0], [2.72, 0.04, 0], [1.78, -0.05, 0],
    [1.75, -0.48, Math.PI / 2], [1.75, -2.48, Math.PI / 2],
  ] as const;
  for (let index = 0; index < fenceSpecs.length; index += 1) {
    const [x, z, yaw] = fenceSpecs[index]!;
    createFenceSegment(pen, `pen-open-fence-${index + 1}`, shared, materials, x, z, yaw, index === 8);
  }
  const openGate = nameNode(new THREE.Group(), "pen-open-red-gate");
  openGate.position.set(1.71, 0.08, -1.52);
  openGate.rotation.y = -1.07;
  addMesh(openGate, "pen-open-gate-frame", createRoundedBlockGeometry(0.74, 0.55, 0.1, 0.08), materials.barn, [0.36, 0.36, 0]);
  addMesh(openGate, "pen-open-gate-diagonal-brace", shared.plankNarrow, materials.barnDark, [0.36, 0.36, 0.07], [0, 0, 0.65], [1.1, 1, 1]);
  pen.add(openGate);

  const feedEdge = nameNode(new THREE.Group(), "pen-visible-feed-edge");
  feedEdge.position.set(1.45, 0.18, -1.92);
  addMesh(feedEdge, "feed-trough-low-open-body", createRoundedBlockGeometry(0.82, 0.23, 0.34, 0.08), materials.wood, [0, 0.16, 0]);
  addMesh(feedEdge, "feed-trough-kernel-face", createRoundedBlockGeometry(0.62, 0.1, 0.22, 0.05), materials.cornShadow, [0, 0.23, 0.03]);
  pen.add(feedEdge);

  const chickens = [
    createChicken(1, shared, materials, materials.whiteChicken, 2.28, -1.98, -0.5),
    createChicken(2, shared, materials, materials.brownChicken, 3.18, -2.2, 0.7),
    createChicken(3, shared, materials, materials.charcoalChicken, 3.46, -0.82, -0.2),
    createChicken(4, shared, materials, materials.brownChicken, 2.48, -0.65, 1.1),
  ] as const;
  for (const chicken of chickens) pen.add(chicken.root);

  const penSign = createSeedTag("pen-chicken-seed-tag-sign", shared, materials, "chicken");
  penSign.position.set(1.78, 0.18, -0.08);
  penSign.rotation.y = -0.05;
  pen.add(penSign);
  occluders.push(penSign.getObjectByName("pen-chicken-seed-tag-sign-cream-notched-face") ?? penSign);
  // Preserve the absolute authored footprint while making the declared service hub the module transform.
  for (const child of pen.children) {
    child.position.x -= 1;
    child.position.z += 1.473;
  }
  pen.position.set(1, 0, -1.473);
  setAuthoredMetadata(pen, "first-yard-chicken-pen-01");
  root.add(pen);
  return chickens;
}

function addEggCluster(parent: THREE.Group, shared: SharedGeometry, materials: SunpatchMaterials, prefix: string, count: number): readonly THREE.Group[] {
  const wovenRingGeometry = new THREE.TorusGeometry(0.27, 0.055, 6, 14);
  addMesh(parent, `${prefix}-woven-outer-ring`, wovenRingGeometry, materials.strawDark, [0, 0.08, 0], [Math.PI / 2, 0, 0]);
  const innerRingGeometry = new THREE.TorusGeometry(0.19, 0.035, 5, 12);
  addMesh(parent, `${prefix}-woven-inner-ring`, innerRingGeometry, materials.straw, [0, 0.12, 0], [Math.PI / 2, 0.3, 0]);
  const positions = [[-0.13, 0], [0.13, 0], [0, 0.14], [0, -0.14]] as const;
  const eggs: THREE.Group[] = [];
  for (let index = 0; index < Math.min(count, positions.length); index += 1) {
    const [x, z] = positions[index]!;
    const egg = nameNode(new THREE.Group(), `${prefix}-egg-unit-${index + 1}`);
    egg.position.set(x, 0.12, z);
    addMesh(egg, `${prefix}-cream-egg-${index + 1}`, shared.egg, index % 2 === 0 ? materials.cream : materials.creamShadow, [0, 0, 0], [0, index * 0.4, 0]);
    addMesh(egg, `${prefix}-egg-seam-${index + 1}`, new THREE.TorusGeometry(0.075, 0.012, 5, 14), materials.straw, [0, 0.015, 0.12], [Math.PI / 2, 0, 0]);
    parent.add(egg);
    eggs.push(egg);
  }
  return eggs;
}

function createNest(root: THREE.Group, materials: SunpatchMaterials, shared: SharedGeometry): readonly THREE.Group[] {
  const nest = nameNode(new THREE.Group(), "landmark-raised-egg-nest");
  nest.position.set(1, 0, -1.273);
  for (const x of [-0.36, 0.36]) {
    addMesh(nest, `nest-leg-${x < 0 ? "left" : "right"}`, shared.plankNarrow, materials.woodDark, [x, 0.38, 0], [0, 0, Math.PI / 2], [1.25, 1, 1]);
  }
  addMesh(nest, "nest-raised-tabletop", shared.plankWide, materials.wood, [0, 0.62, 0], [0, 0, 0], [1.08, 1, 1.2]);
  const eggs = nameNode(new THREE.Group(), "nest-visible-egg-output");
  eggs.position.set(0, 0.7, 0);
  eggs.scale.setScalar(1.15);
  const nestEggs = addEggCluster(eggs, shared, materials, "nest", 4);
  for (const egg of nestEggs) egg.visible = false;
  nest.add(eggs);
  const sign = createSeedTag("nest-egg-seed-tag-sign", shared, materials, "egg");
  sign.position.set(0.58, 0.12, 0.12);
  sign.scale.setScalar(0.86);
  nest.add(sign);
  for (const child of nest.children) {
    child.position.x += 0.18;
    child.position.y += 0.14;
    child.position.z += 0.623;
  }
  setAuthoredMetadata(nest, "first-yard-raised-egg-nest");
  root.add(nest);
  return nestEggs;
}

function createMarket(root: THREE.Group, materials: SunpatchMaterials, shared: SharedGeometry, occluders: THREE.Object3D[]): FirstYardMarketRig {
  const market = nameNode(new THREE.Group(), "landmark-roadside-market-01");
  market.position.set(-1.551, 0, 2.945);
  const legs = [[-0.82, -0.24], [0.82, -0.24], [-0.82, 0.24], [0.82, 0.24]] as const;
  for (let index = 0; index < legs.length; index += 1) {
    const [x, z] = legs[index]!;
    addMesh(market, `market-leg-${index + 1}`, shared.plankMedium, materials.woodDark, [x, 0.32, z], [0, 0, Math.PI / 2], [1.08, 1, 1]);
  }
  addMesh(market, "market-low-working-counter", createRoundedBlockGeometry(2.05, 0.24, 0.68, 0.07), materials.wood, [0, 0.68, 0]);
  for (let plank = 0; plank < 3; plank += 1) {
    addMesh(market, `market-intentional-counter-plank-${plank + 1}`, shared.plankMedium, plank === 1 ? materials.woodLight : materials.woodDark, [-0.68 + plank * 0.68, 0.74, -0.01], [0, 0, 0], [0.92, 0.7, 1.4]);
  }
  const clothGeometry = createRoundedBlockGeometry(0.74, 0.72, 0.035, 0.06, 0.006);
  const cloth = addMesh(market, "market-teal-sun-stitch-cloth", clothGeometry, materials.tealCloth, [0.15, 0.62, -0.37], [Math.PI / 2, 0, 0.03]);
  addSunStitch(cloth, materials, 0.22, [0, 0.02, 0.04], [Math.PI / 2, 0, 0]);

  const tray = nameNode(new THREE.Group(), "market-egg-only-woven-receiving-tray");
  tray.position.set(-0.48, 0.82, 0);
  tray.scale.setScalar(0.92);
  addEggCluster(tray, shared, materials, "market-tray", 0);
  market.add(tray);
  const displayTray = nameNode(new THREE.Group(), "market-small-egg-display-tray");
  displayTray.position.set(0.48, 0.82, 0.02);
  displayTray.scale.setScalar(0.64);
  addEggCluster(displayTray, shared, materials, "market-display", 1);
  market.add(displayTray);

  const canopy = nameNode(new THREE.Group(), "market-offset-open-awning");
  for (const x of [-0.93, 0.93]) addMesh(canopy, `market-awning-post-${x < 0 ? "left" : "right"}`, shared.fencePost, materials.woodDark, [x, 1.25, 0.28], [0, 0, 0], [1.05, 2.9, 1.05]);
  addMesh(canopy, "market-awning-offset-peak", createRoundedBlockGeometry(2.15, 0.16, 0.7, 0.06), materials.barn, [0.12, 2.18, 0.15], [-0.09, 0, 0.03]);
  addMesh(canopy, "market-awning-cream-edge", createRoundedBlockGeometry(2.22, 0.08, 0.12, 0.03), materials.cream, [0.12, 1.88, -0.22]);
  market.add(canopy);
  occluders.push(canopy);

  const cashLanding = nameNode(new THREE.Group(), "market-cash-landing");
  cashLanding.position.set(0.68, 0.86, -0.08);
  for (let layer = 0; layer < 3; layer += 1) {
    addMesh(cashLanding, `cash-folded-bundle-layer-${layer + 1}`, createRoundedBlockGeometry(0.4, 0.07, 0.26, 0.035), materials.cash, [0, layer * 0.055, 0], [0, layer * 0.03, 0]);
  }
  addMesh(cashLanding, "cash-bundle-tie", createRoundedBlockGeometry(0.07, 0.25, 0.3, 0.025), materials.straw, [0, 0.1, 0], [0, 0, Math.PI / 2]);
  market.add(cashLanding);
  cashLanding.visible = false;

  const sign = createSeedTag("market-egg-only-seed-tag-sign", shared, materials, "market");
  sign.position.set(1.05, 0.76, 0.28);
  market.add(sign);
  setAuthoredMetadata(market, "first-yard-roadside-market-01");
  root.add(market);
  return { cash: cashLanding, receiver: tray };
}

function addSunStitch(
  parent: THREE.Object3D,
  materials: SunpatchMaterials,
  radius: number,
  position: readonly [number, number, number],
  rotation: readonly [number, number, number] = [0, 0, 0],
): THREE.Group {
  const stitch = nameNode(new THREE.Group(), `${parent.name}-four-ray-sun-stitch`);
  stitch.position.set(...position);
  stitch.rotation.set(...rotation);
  addMesh(stitch, `${stitch.name}-center`, new THREE.CylinderGeometry(radius * 0.28, radius * 0.28, 0.025, 10), materials.corn, [0, 0, 0], [Math.PI / 2, 0, 0]);
  for (let ray = 0; ray < 4; ray += 1) {
    const angle = ray * Math.PI / 2;
    const geometry = createRoundedBlockGeometry(radius * 0.48, radius * 0.13, 0.022, radius * 0.045, 0.004);
    addMesh(stitch, `${stitch.name}-ray-${ray + 1}`, geometry, materials.corn, [Math.cos(angle) * radius * 0.45, Math.sin(angle) * radius * 0.45, 0], [0, 0, angle]);
  }
  parent.add(stitch);
  return stitch;
}

function createCargoUnit(
  index: number,
  materials: SunpatchMaterials,
  shared: SharedGeometry,
): FirstYardCargoUnitRig {
  const root = nameNode(new THREE.Group(), `player-cargo-slot-${index + 1}`);
  root.position.set(0.48, 0.64 + index * 0.31, 0.14);
  root.rotation.z = index % 2 === 0 ? -0.025 : 0.025;
  root.visible = false;

  const corn = nameNode(new THREE.Group(), `${root.name}-corn-unit`);
  addMesh(corn, `${root.name}-corn-gold-body`, shared.cornCob, index % 2 === 0 ? materials.corn : materials.cornShadow, [0, 0, 0], [0, index * 0.47, Math.PI / 2], [0.62, 0.62, 0.62]);
  addMesh(corn, `${root.name}-corn-husk-left`, shared.cornLeaf, materials.leaf, [-0.12, -0.03, 0.03], [0.2, -0.4, 0.72], [0.42, 0.44, 0.4]);
  addMesh(corn, `${root.name}-corn-husk-right`, shared.cornLeaf, materials.leafLight, [0.12, -0.03, 0.03], [0.2, 0.4, -0.72], [0.42, 0.44, 0.4]);
  root.add(corn);

  const egg = nameNode(new THREE.Group(), `${root.name}-egg-unit`);
  egg.visible = false;
  addMesh(egg, `${root.name}-egg-cream-body`, shared.egg, index % 2 === 0 ? materials.cream : materials.creamShadow, [0, 0.01, 0], [0, index * 0.31, Math.PI / 2], [0.86, 0.86, 0.86]);
  addMesh(egg, `${root.name}-egg-warm-seam`, new THREE.TorusGeometry(0.08, 0.012, 5, 14), materials.straw, [0, 0.02, 0.13], [Math.PI / 2, 0, 0]);
  root.add(egg);
  return { corn, egg, root };
}

function createPenExtensionPad(materials: SunpatchMaterials, shared: SharedGeometry): FirstYardManualChainRig["penExtensionPad"] {
  const root = nameNode(new THREE.Group(), "landmark-pad-pen-extension-01");
  root.position.set(-3.853, 0, 2.945);
  addMesh(root, "pen-extension-pad-scalloped-stone-base", new THREE.CylinderGeometry(0.78, 0.88, 0.17, 8), materials.stone, [0, 0.085, 0]);
  addMesh(root, "pen-extension-pad-inset-cream-face", new THREE.CylinderGeometry(0.65, 0.7, 0.11, 8), materials.creamShadow, [0, 0.19, 0]);

  const locked = nameNode(new THREE.Group(), "pen-extension-pad-locked-preview");
  addMesh(locked, "pen-extension-pad-locked-brass-ring", new THREE.TorusGeometry(0.61, 0.052, 8, 28), materials.strawDark, [0, 0.275, 0], [Math.PI / 2, 0, 0]);
  const lockTag = createSeedTag("pen-extension-pad-seed-tag", shared, materials, "chicken");
  lockTag.position.set(0, 0.26, 0);
  lockTag.rotation.x = -Math.PI / 2;
  lockTag.scale.setScalar(0.62);
  locked.add(lockTag);
  const lock = nameNode(new THREE.Group(), "pen-extension-pad-lock-cue");
  lock.position.set(0, 0.33, -0.04);
  addMesh(lock, "pen-extension-pad-lock-body", createRoundedBlockGeometry(0.27, 0.23, 0.08, 0.055), materials.ink, [0, 0, 0]);
  addMesh(lock, "pen-extension-pad-lock-shackle", new THREE.TorusGeometry(0.1, 0.026, 6, 16, Math.PI), materials.ink, [0, 0.14, 0], [0, 0, 0]);
  locked.add(lock);
  root.add(locked);

  const active = nameNode(new THREE.Group(), "pen-extension-pad-active-affordance");
  active.visible = false;
  const ring = addMesh(active, "pen-extension-pad-active-teal-ring", new THREE.TorusGeometry(0.62, 0.065, 8, 32), materials.focus, [0, 0.285, 0], [Math.PI / 2, 0, 0]);
  const chevrons = nameNode(new THREE.Group(), "pen-extension-pad-active-chevrons");
  for (let index = 0; index < 3; index += 1) {
    const chevron = nameNode(new THREE.Group(), `pen-extension-pad-chevron-${index + 1}`);
    chevron.position.set(0, 0.72 + index * 0.2, 0);
    addMesh(chevron, `${chevron.name}-left`, createRoundedBlockGeometry(0.25, 0.07, 0.045, 0.024), materials.focus, [-0.09, 0, 0], [0, 0, -0.62]);
    addMesh(chevron, `${chevron.name}-right`, createRoundedBlockGeometry(0.25, 0.07, 0.045, 0.024), materials.focus, [0.09, 0, 0], [0, 0, 0.62]);
    chevrons.add(chevron);
  }
  active.add(chevrons);
  const price = nameNode(new THREE.Group(), "pen-extension-pad-price-24-cash");
  price.position.set(0, 0.35, 0);
  for (let index = 0; index < 4; index += 1) {
    addMesh(price, `pen-extension-pad-price-token-${index + 1}`, new THREE.CylinderGeometry(0.075, 0.075, 0.025, 12), materials.gold, [(index - 1.5) * 0.12, 0, 0], [Math.PI / 2, 0, 0]);
  }
  active.add(price);
  root.add(active);
  setAuthoredMetadata(root, "first-yard-pad-pen-extension-01");
  return { active, chevrons, locked, ring, root };
}

function createManualChainRig(
  root: THREE.Group,
  player: FirstYardPlayerRig,
  pen: THREE.Group,
  plots: readonly FirstYardPlotRig[],
  nestEggs: readonly THREE.Group[],
  market: FirstYardMarketRig,
  materials: SunpatchMaterials,
  shared: SharedGeometry,
): FirstYardManualChainRig {
  const cargo = Array.from({ length: 5 }, (_, index) => createCargoUnit(index, materials, shared));
  for (const unit of cargo) player.root.add(unit.root);

  const feedEdge = pen.getObjectByName("pen-visible-feed-edge");
  if (!(feedEdge instanceof THREE.Group)) throw new Error("Missing pen feed edge");
  const stagedCorn = Array.from({ length: 4 }, (_, index) => {
    const unit = nameNode(new THREE.Group(), `pen-staged-corn-${index + 1}`);
    unit.position.set((index - 1.5) * 0.16, 0.34 + (index % 2) * 0.05, 0);
    unit.rotation.set(0.05, index * 0.7, Math.PI / 2 + (index - 1.5) * 0.05);
    unit.visible = false;
    addMesh(unit, `${unit.name}-gold-body`, shared.cornCob, index % 2 === 0 ? materials.corn : materials.cornShadow, [0, 0, 0], [0, 0, 0], [0.5, 0.5, 0.5]);
    addMesh(unit, `${unit.name}-green-husk`, shared.cornLeaf, materials.leaf, [0, -0.1, 0.03], [0, 0.25, 0.4], [0.34, 0.34, 0.34]);
    feedEdge.add(unit);
    return unit;
  });

  const penExtensionPad = createPenExtensionPad(materials, shared);
  root.add(penExtensionPad.root);
  return {
    cargo,
    marketCash: market.cash,
    marketReceiver: market.receiver,
    nestEggs,
    penExtensionPad,
    plots,
    stagedCorn,
  };
}

function createGuidedPad(root: THREE.Group, materials: SunpatchMaterials): FirstYardGuidedPad {
  const pad = nameNode(new THREE.Group(), "landmark-pad-bigger-basket-01");
  pad.position.set(-1.551, 0, 0.643);
  addMesh(pad, "guided-pad-octagonal-stone-base", new THREE.CylinderGeometry(0.82, 0.9, 0.16, 8, 1, false), materials.stone, [0, 0.08, 0]);
  addMesh(pad, "guided-pad-inset-gold-face", new THREE.CylinderGeometry(0.66, 0.71, 0.12, 8, 1, false), materials.gold, [0, 0.2, 0]);
  const ring = addMesh(pad, "guided-pad-teal-focus-ring", new THREE.TorusGeometry(0.65, 0.055, 8, 32), materials.focus, [0, 0.28, 0], [Math.PI / 2, 0, 0]);
  const chevrons = nameNode(new THREE.Group(), "guided-pad-focus-chevrons");
  for (let index = 0; index < 3; index += 1) {
    const chevron = nameNode(new THREE.Group(), `guided-pad-chevron-${index + 1}`);
    chevron.position.set(0, 0.7 + index * 0.22, 0);
    addMesh(chevron, `${chevron.name}-left-stroke`, createRoundedBlockGeometry(0.28, 0.07, 0.045, 0.025), materials.focus, [-0.1, 0, 0], [0, 0, -0.62]);
    addMesh(chevron, `${chevron.name}-right-stroke`, createRoundedBlockGeometry(0.28, 0.07, 0.045, 0.025), materials.focus, [0.1, 0, 0], [0, 0, 0.62]);
    chevrons.add(chevron);
  }
  pad.add(chevrons);
  const basketIcon = nameNode(new THREE.Group(), "guided-pad-bigger-basket-icon");
  basketIcon.position.set(0, 0.305, 0);
  addMesh(basketIcon, "guided-pad-basket-dark-woven-body", createRoundedBlockGeometry(0.6, 0.34, 0.055, 0.1, 0.008), materials.ink, [0, 0, 0], [-Math.PI / 2, 0, 0]);
  addMesh(basketIcon, "guided-pad-basket-cream-rim", new THREE.TorusGeometry(0.27, 0.035, 6, 18), materials.cream, [0, 0.04, 0], [Math.PI / 2, 0, 0], [1, 1, 0.6]);
  for (let slat = 0; slat < 3; slat += 1) {
    addMesh(basketIcon, `guided-pad-basket-woven-slat-${slat + 1}`, createRoundedBlockGeometry(0.035, 0.24, 0.025, 0.012, 0.003), materials.straw, [(slat - 1) * 0.16, 0.055, 0], [-Math.PI / 2, 0, 0]);
  }
  addMesh(basketIcon, "guided-pad-basket-raised-handle", new THREE.TorusGeometry(0.25, 0.035, 6, 18, Math.PI), materials.ink, [0, 0.08, -0.04], [0, 0, Math.PI]);
  pad.add(basketIcon);
  setAuthoredMetadata(pad, "first-yard-guided-pad-bigger-basket");
  root.add(pad);
  return { chevrons, ring, root: pad };
}

function createLockedGate(root: THREE.Group, materials: SunpatchMaterials, shared: SharedGeometry, occluders: THREE.Object3D[]): void {
  const gate = nameNode(new THREE.Group(), "landmark-yard-gate-01-locked");
  gate.position.set(0, 0, 5.4);
  const stoneBlock = createRoundedBlockGeometry(0.48, 0.5, 0.52, 0.1);
  for (const side of [-1, 1] as const) {
    const pillar = nameNode(new THREE.Group(), `locked-gate-${side < 0 ? "left" : "right"}-stone-pillar`);
    pillar.position.x = side * 0.9;
    for (let layer = 0; layer < 3; layer += 1) {
      addMesh(pillar, `${pillar.name}-block-${layer + 1}`, stoneBlock, layer % 2 === 0 ? materials.stoneLight : materials.stone, [side * (layer % 2) * 0.04, 0.25 + layer * 0.45, 0], [0, layer * 0.06, 0]);
    }
    gate.add(pillar);
  }
  const doors = nameNode(new THREE.Group(), "locked-north-gate-double-doors");
  for (const side of [-1, 1] as const) {
    const door = nameNode(new THREE.Group(), `locked-gate-${side < 0 ? "left" : "right"}-door`);
    door.position.set(side * 0.4, 0.62, 0);
    for (let plank = 0; plank < 3; plank += 1) {
      addMesh(door, `${door.name}-modeled-plank-${plank + 1}`, createRoundedBlockGeometry(0.25, 1.18 - Math.abs(plank - 1) * 0.08, 0.14, 0.055), plank === 1 ? materials.woodLight : materials.wood, [(plank - 1) * 0.25 * side, 0, 0]);
    }
    addMesh(door, `${door.name}-diagonal-brace`, shared.plankWide, materials.woodDark, [0, -0.04, 0.1], [0, 0, side * -0.7], [0.94, 0.74, 0.9]);
    doors.add(door);
  }
  gate.add(doors);
  addSunStitch(doors, materials, 0.28, [0, 0.18, 0.16]);
  const lock = nameNode(new THREE.Group(), "locked-gate-brass-padlock");
  lock.position.set(0, 0.87, -0.2);
  addMesh(lock, "locked-gate-cream-seed-tag-backing", createRoundedBlockGeometry(0.72, 0.6, 0.09, 0.12), materials.cream, [0, 0.02, 0.08]);
  addMesh(lock, "locked-gate-diagonal-brass-crossbar", createRoundedBlockGeometry(1.04, 0.14, 0.12, 0.045), materials.gold, [0, -0.18, 0.01], [0, 0, -0.08]);
  addMesh(lock, "locked-gate-padlock-body", createRoundedBlockGeometry(0.5, 0.48, 0.16, 0.1), materials.gold, [0, 0, -0.04]);
  addMesh(lock, "locked-gate-padlock-shackle", new THREE.TorusGeometry(0.21, 0.06, 8, 20, Math.PI), materials.gold, [0, 0.28, -0.04], [0, 0, 0]);
  addMesh(lock, "locked-gate-keyhole", createPearGeometry(0.085, 0.16, 10), materials.ink, [0, -0.12, -0.13], [Math.PI / 2, 0, 0]);
  gate.add(lock);
  occluders.push(doors);
  setAuthoredMetadata(gate, "first-yard-yard-gate-01-locked");
  root.add(gate);
}

function createPlayer(materials: SunpatchMaterials, shared: SharedGeometry): FirstYardPlayerRig {
  const root = nameNode(new THREE.Group(), "sunpatch-player-root");
  root.position.set(-4.102, 0, -1.473);
  root.rotation.y = 0.62;

  const body = nameNode(new THREE.Group(), "sunpatch-player-body-joint");
  body.position.y = 0.18;
  addMesh(body, "sunpatch-player-pear-torso", createPearGeometry(0.61, 0.92, 18), materials.tealCloth, [0, 0.57, 0], [0, 0, 0]);
  addMesh(body, "sunpatch-player-belt", new THREE.TorusGeometry(0.31, 0.045, 7, 20), materials.strawDark, [0, 0.68, 0], [Math.PI / 2, 0, 0], [1, 1, 0.82]);
  addMesh(body, "sunpatch-player-cream-neckerchief-left", shared.leaf, materials.cream, [-0.1, 1.16, -0.25], [0.13, 0.2, 2.55], [0.52, 0.56, 0.5]);
  addMesh(body, "sunpatch-player-cream-neckerchief-right", shared.leaf, materials.cream, [0.1, 1.16, -0.25], [0.13, -0.2, -2.55], [0.52, 0.56, 0.5]);
  const patch = addMesh(body, "sunpatch-player-asymmetric-sun-patch", createRoundedBlockGeometry(0.25, 0.25, 0.025, 0.06, 0.004), materials.cream, [0.32, 0.96, -0.28], [0.08, 0, -0.08]);
  addSunStitch(patch, materials, 0.11, [0, 0, -0.025]);
  root.add(body);

  const head = nameNode(new THREE.Group(), "sunpatch-player-head-joint");
  head.position.set(0, 1.78, -0.02);
  addMesh(head, "sunpatch-player-head-31-percent", new THREE.SphereGeometry(0.36, 20, 14), materials.skin, [0, 0, 0], [0, 0, 0], [0.94, 1.02, 0.9]);
  const hairGeometry = new THREE.SphereGeometry(0.13, 10, 8);
  for (let curl = 0; curl < 5; curl += 1) {
    const angle = -0.85 + curl * 0.42;
    addMesh(head, `sunpatch-player-hair-curl-${curl + 1}`, hairGeometry, materials.hair, [Math.sin(angle) * 0.33, 0.02 - Math.abs(curl - 2) * 0.03, 0.2 + Math.cos(angle) * 0.1]);
  }
  const eyeGeometry = new THREE.SphereGeometry(0.035, 8, 6);
  addMesh(head, "sunpatch-player-left-eye", eyeGeometry, materials.ink, [-0.13, 0.035, -0.31]);
  addMesh(head, "sunpatch-player-right-eye", eyeGeometry, materials.ink, [0.13, 0.035, -0.31]);
  addMesh(head, "sunpatch-player-left-brow", createRoundedBlockGeometry(0.1, 0.025, 0.025, 0.01, 0.003), materials.hair, [-0.13, 0.12, -0.315], [0, 0, -0.08]);
  addMesh(head, "sunpatch-player-right-brow", createRoundedBlockGeometry(0.1, 0.025, 0.025, 0.01, 0.003), materials.hair, [0.13, 0.12, -0.315], [0, 0, 0.08]);
  const hat = nameNode(new THREE.Group(), "sunpatch-player-straw-hat");
  hat.position.set(0, 0.28, 0);
  addMesh(hat, "sunpatch-player-hat-wide-brim", new THREE.CylinderGeometry(0.52, 0.55, 0.09, 24), materials.straw, [0, 0, 0], [0, 0, 0], [1.04, 1, 0.86]);
  addMesh(hat, "sunpatch-player-hat-rounded-crown", createPearGeometry(0.38, 0.35, 18), materials.straw, [0, 0.02, 0], [0, 0, 0], [1, 1, 0.88]);
  addMesh(hat, "sunpatch-player-hat-woven-seam", new THREE.TorusGeometry(0.34, 0.027, 6, 22), materials.strawDark, [0, 0.14, 0], [Math.PI / 2, 0, 0], [1, 1, 0.88]);
  head.add(hat);
  root.add(head);

  const createArm = (side: "left" | "right"): THREE.Group => {
    const sign = side === "left" ? -1 : 1;
    const arm = nameNode(new THREE.Group(), `sunpatch-player-${side}-arm-joint`);
    arm.position.set(sign * 0.42, 1.3, -0.01);
    addMesh(arm, `sunpatch-player-${side}-rolled-sleeve`, createPearGeometry(0.2, 0.45, 12), materials.tealCloth, [0, -0.33, 0], [0, 0, sign * -0.12]);
    addMesh(arm, `sunpatch-player-${side}-large-mitten-hand`, shared.mitten, materials.skin, [sign * 0.02, -0.62, -0.01], [0, 0, sign * -0.08]);
    root.add(arm);
    return arm;
  };
  const leftArm = createArm("left");
  const rightArm = createArm("right");

  const createLeg = (side: "left" | "right"): THREE.Group => {
    const sign = side === "left" ? -1 : 1;
    const leg = nameNode(new THREE.Group(), `sunpatch-player-${side}-leg-joint`);
    leg.position.set(sign * 0.2, 0.66, 0.02);
    addMesh(leg, `sunpatch-player-${side}-trouser-leg`, createRoundedBlockGeometry(0.27, 0.48, 0.28, 0.09), materials.brownCloth, [0, -0.25, 0]);
    const boot = addMesh(leg, `sunpatch-player-${side}-short-planted-boot`, shared.boot, materials.woodDark, [0, -0.54, -0.08], [0, 0, 0]);
    boot.scale.set(1, 1, 1.08);
    root.add(leg);
    return leg;
  };
  const leftLeg = createLeg("left");
  const rightLeg = createLeg("right");
  const dustAnchor = nameNode(new THREE.Object3D(), "sunpatch-player-footstep-dust-anchor");
  dustAnchor.position.set(0, 0.02, 0.16);
  root.add(dustAnchor);
  setAuthoredMetadata(root, "first-yard-sunpatch-player");
  return { body, dustAnchor, leftArm, leftLeg, rightArm, rightLeg, root };
}

export function createFirstYardVisualKit(): FirstYardVisualKit {
  const root = nameNode(new THREE.Group(), "first-yard-visual-kit-root");
  setAuthoredMetadata(root, "first-yard-visual-kit");
  root.userData.factoryId = "first-yard-walkabout-v1";
  root.userData.shippingAdmission = "manual-chain-production-art-candidate";
  const materials = createSunpatchMaterials();
  const shared = createSharedGeometry();
  const occluders: THREE.Object3D[] = [];
  const orphanedGeometries = new Set<THREE.BufferGeometry>();

  createGround(root, materials, shared);
  createPathNetwork(root, materials);
  createPerimeter(root, materials, shared);
  createFoliage(root, materials, shared, occluders);
  const corn = createField(root, materials, shared);
  const chickens = createPen(root, materials, shared, occluders);
  const pen = root.getObjectByName("landmark-chicken-pen-01");
  if (!(pen instanceof THREE.Group)) throw new Error("Missing chicken pen");
  const nestEggs = createNest(root, materials, shared);
  const market = createMarket(root, materials, shared, occluders);
  const guidedPad = createGuidedPad(root, materials);
  createLockedGate(root, materials, shared, occluders);
  const player = createPlayer(materials, shared);
  root.add(player.root);
  const manualChain = createManualChainRig(root, player, pen, corn, nestEggs, market, materials, shared);

  for (const plot of manualChain.plots) {
    batchStaticMeshes(plot.clearedCue, `${plot.clearedCue.name}-batch`, new Set(), orphanedGeometries);
    batchStaticMeshes(plot.cooldownCue, `${plot.cooldownCue.name}-batch`, new Set(), orphanedGeometries);
  }
  for (const unit of manualChain.cargo) {
    batchStaticMeshes(unit.corn, `${unit.corn.name}-batch`, new Set(), orphanedGeometries);
    batchStaticMeshes(unit.egg, `${unit.egg.name}-batch`, new Set(), orphanedGeometries);
  }
  for (const unit of manualChain.stagedCorn) {
    batchStaticMeshes(unit, `${unit.name}-batch`, new Set(), orphanedGeometries);
  }
  for (const egg of manualChain.nestEggs) {
    batchStaticMeshes(egg, `${egg.name}-batch`, new Set(), orphanedGeometries);
  }
  batchStaticMeshes(manualChain.penExtensionPad.locked, "pen-extension-pad-locked-batch", new Set(), orphanedGeometries);
  batchStaticMeshes(manualChain.penExtensionPad.root, "pen-extension-pad-base-batch", new Set([
    manualChain.penExtensionPad.locked,
    manualChain.penExtensionPad.active,
  ]), orphanedGeometries);
  batchStaticMeshes(manualChain.penExtensionPad.chevrons, "pen-extension-pad-chevron-batch", new Set(), orphanedGeometries);
  const padPrice = manualChain.penExtensionPad.active.getObjectByName("pen-extension-pad-price-24-cash");
  if (!(padPrice instanceof THREE.Group)) throw new Error("Missing Pen Extension price cue");
  batchStaticMeshes(padPrice, "pen-extension-pad-price-batch", new Set(), orphanedGeometries);
  batchStaticMeshes(manualChain.penExtensionPad.active, "pen-extension-pad-active-batch", new Set([
    manualChain.penExtensionPad.chevrons,
    padPrice,
  ]), orphanedGeometries);

  for (const entry of corn) {
    batchStaticMeshes(entry.leaves, `${entry.root.name}-leaf-batch`, new Set(), orphanedGeometries);
  }
  for (const entry of chickens) {
    batchStaticMeshes(entry.root, `${entry.root.name}-body-batch`, new Set([entry.head, entry.leftWing, entry.rightWing]), orphanedGeometries);
    batchStaticMeshes(entry.head, `${entry.root.name}-head-batch`, new Set(), orphanedGeometries);
  }
  batchStaticMeshes(player.body, "sunpatch-player-body-batch", new Set(), orphanedGeometries);
  const playerHead = player.root.getObjectByName("sunpatch-player-head-joint");
  if (!(playerHead instanceof THREE.Group)) throw new Error("Missing player head joint");
  batchStaticMeshes(playerHead, "sunpatch-player-head-batch", new Set(), orphanedGeometries);
  batchStaticMeshes(guidedPad.chevrons, "guided-pad-chevron-batch", new Set(), orphanedGeometries);
  const gateDoors = root.getObjectByName("locked-north-gate-double-doors");
  if (!(gateDoors instanceof THREE.Group)) throw new Error("Missing locked gate doors");
  batchStaticMeshes(gateDoors, "locked-gate-door-batch", new Set(), orphanedGeometries);

  const animatedOrOccluding = new Set<THREE.Object3D>([
    ...occluders,
    ...corn.map((entry) => entry.leaves),
    ...corn.flatMap((entry) => [entry.clearedCue, entry.cooldownCue]),
    ...chickens.map((entry) => entry.root),
    guidedPad.ring,
    guidedPad.chevrons,
    ...manualChain.plots.flatMap((entry) => [entry.clearedCue, entry.cooldownCue]),
    ...manualChain.cargo.map((entry) => entry.root),
    ...manualChain.stagedCorn,
    ...manualChain.nestEggs,
    manualChain.marketCash,
    manualChain.penExtensionPad.root,
    player.root,
    root.getObjectByName("locked-gate-brass-padlock")!,
  ]);
  const batchableLandmarks = [
    "landmark-first-yard-ground",
    "landmark-authored-path-ribbons",
    "landmark-detailed-yard-perimeter",
    "landmark-perimeter-foliage-and-props",
    "landmark-field-row-01",
    "landmark-chicken-pen-01",
    "landmark-raised-egg-nest",
    "landmark-roadside-market-01",
    "landmark-pad-bigger-basket-01",
    "landmark-yard-gate-01-locked",
  ] as const;
  for (const landmarkName of batchableLandmarks) {
    const landmark = root.getObjectByName(landmarkName);
    if (!(landmark instanceof THREE.Group)) throw new Error(`Missing batchable landmark: ${landmarkName}`);
    batchStaticMeshes(landmark, `${landmarkName}-static-batch`, animatedOrOccluding, orphanedGeometries);
  }
  const disposeOwnedResources = collectAndDisposeOwnedResources(root, orphanedGeometries);
  return {
    chickens,
    corn,
    dispose: disposeOwnedResources,
    guidedPad,
    manualChain,
    occluders,
    player,
    root,
  };
}
